import { Extension } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { prepareFileUpload, uploadFileToAzure } from '../../../../services/wikiServices/WikiFileService';
import { FileUploadPrepareRequest } from '../../../../interfaces/file/FileUpload';

interface ImageUploadOptions {
  uploadFile: (file: File) => Promise<string | null>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        imageUpload: {
            setImageUpload: (options: { src: string; alt?: string; title?: string; uploadStatus?: 'uploading' | 'completed' }) => ReturnType;
            openImageFilePicker: () => ReturnType;
        };
    }
}

export const ImageUpload = Extension.create<ImageUploadOptions>({
    name: 'imageUpload',

    addOptions() {
        return {
            uploadFile: async () => null,
        };
    },

    addCommands() {
        return {
            setImageUpload: (options) => ({ commands }) => {
                return commands.insertContent({
                    type: 'image',
                    attrs: { ...options, 'data-upload-status': options.uploadStatus || 'completed' },
                });
            },
            openImageFilePicker: () => ({ editor }) => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = async (event) => {
                    const file = (event.target as HTMLInputElement).files?.[0];
                    if (file) {
                        const reader = new FileReader();
                        reader.onload = (readerEvent) => {
                            const src = readerEvent.target?.result as string;
                            editor.commands.setImageUpload({ src, uploadStatus: 'uploading' });
                        };
                        reader.readAsDataURL(file);

                        this.options.uploadFile(file).then((url) => {
                            if (url) {
                                const { doc } = editor.state;
                                let pos = -1;
                                doc.descendants((node: ProseMirrorNode, p: number) => {
                                    if (node.type.name === 'image' && node.attrs.src.startsWith('data:image') && node.attrs['data-upload-status'] === 'uploading') {
                                        pos = p;
                                        return false;
                                    }
                                    return true;
                                });

                                if (pos !== -1) {
                                    editor.chain().deleteRange({ from: pos, to: pos + 1 }).insertContentAt(pos, {
                                        type: 'image',
                                        attrs: { src: url, 'data-upload-status': 'completed' },
                                    }).run();
                                } else {
                                    editor.commands.setImageUpload({ src: url, uploadStatus: 'completed' });
                                }
                            }
                        });
                    }
                };
                input.click();
                return true;
            },
        };
    },

    addProseMirrorPlugins() {
        const upload = this.options.uploadFile;
        return [
            new Plugin({
                props: {
                    handleDrop: (view, event, _slice, moved) => {
                        if (moved || !event.dataTransfer || !event.dataTransfer.files.length) {
                            return false;
                        }
                        const file = event.dataTransfer.files[0];
                        if (file.type.startsWith('image/')) {
                            const { editor } = this;
                            const reader = new FileReader();
                            reader.onload = (readerEvent) => {
                                const src = readerEvent.target?.result as string;
                                editor.commands.setImageUpload({ src, uploadStatus: 'uploading' });
                            };
                            reader.readAsDataURL(file);

                            upload(file).then((url) => {
                                if (url) {
                                    const { doc } = editor.state;
                                    let pos = -1;
                                    doc.descendants((node: ProseMirrorNode, p: number) => {
                                        if (node.type.name === 'image' && node.attrs.src.startsWith('data:image') && node.attrs['data-upload-status'] === 'uploading') {
                                            pos = p;
                                            return false;
                                        }
                                        return true;
                                    });

                                    if (pos !== -1) {
                                        editor.chain().deleteRange({ from: pos, to: pos + 1 }).insertContentAt(pos, {
                                            type: 'image',
                                            attrs: { src: url, 'data-upload-status': 'completed' },
                                        }).run();
                                    } else {
                                        editor.commands.setImageUpload({ src: url, uploadStatus: 'completed' });
                                    }
                                }
                            });
                            return true;
                        }
                        return false;
                    },
                },
            }),
        ];
    },
});
